import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "../../../styles/pages/ouber.scss";
import { Tag, Space, Button, Typography, Alert, Input, Row, Col, Select, DatePicker } from "antd";
import {
  EyeOutlined,
  EditOutlined,
  PlusCircleOutlined,
  SearchOutlined,
  PrinterOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import PvTable from "../../../components/Table/PvTable";
import DataView from "../../../components/DataView/DataView";
import PvCardList from "../../../components/DataView/PvCardList";
import AdminAddTripModal from "../../../components/Ouber/modals/admin/AdminAddTripModal";
import AdminEditTripModal from "../../../components/Ouber/modals/admin/AdminEditTripModal";
import AdminViewTripModal from "../../../components/Ouber/modals/admin/AdminViewTripModal";
import AdminConfirmDeleteTripModal from "../../../components/Ouber/modals/admin/AdminConfirmDeleteTripModal";
import { showSuccessToast, showErrorToast } from "../../../components/Ouber/ToastNotification";
import { useOuberService } from "../../../services";
import { mapUnitToAcronym } from "../../../utils/ouberUtils";

/**
 * The purpose of this page is to allow administrators (Super Admin and Unit Admins) 
 * to manage the entire lifecycle of trips, including creation, editing, and deletion.
 */
const ManageTripsPage = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const { dataView } = useSelector((state) => state.generalSlice);
  const { details: profile, account_types = [] } = useSelector((state) => state.accountDetailSlice);

  const userUnits = useMemo(() => {
    if (!profile) return [];
    const todayStr = dayjs().format("YYYY-MM-DD");
    const units = (profile.positions || [])
      .filter(p => {
        const isAO = [
          'Administrative Officer I',
          'Administrative Officer II',
          'Administrative Officer III',
          'Administrative Officer IV',
          'Administrative Officer V',
        ].includes(p.position_name);
        if (!isAO) return false;

        const endDate = p.pivot?.position_end_date || p.position_end_date;
        if (!endDate) return true;
        return endDate >= todayStr;
      })
      .map(p => mapUnitToAcronym(p.unit))
      .filter(Boolean);

    if (units.length === 0 && profile.department) {
      return [mapUnitToAcronym(profile.department)];
    }
    
    return units;
  }, [profile]);
  const isSuperAdmin = profile?.account_type === "Super Admin" || account_types.includes("superadmin") || account_types.includes("xadmin");

  const [filterLocation, setFilterLocation] = useState("");
  const [filterDriver, setFilterDriver] = useState("");
  const [filterDate, setFilterDate] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);



  const { service: ouberService, loading } = useOuberService();
  const [data, setData] = useState([]);

  // Fetches trips from the backend with support for location, driver, date, and status filters
  const fetchTrips = useCallback(async () => {
    try {
      const params = { 
        location: filterLocation || undefined,
        driver_name: filterDriver || undefined,
        date: filterDate ? filterDate.format("YYYY-MM-DD") : undefined,
        status: filterStatus || undefined,
        scope: "unit"
      };
      const response = await ouberService.getTrips(params);
      
      const tripsListRaw = Array.isArray(response) ? response : (response?.data || []);
      const tripsList = tripsListRaw.filter(t => t !== null && t !== undefined);
      
      const trips = tripsList.map(trip => ({ 
        ...trip, 
        key: trip.trip_id.toString(), 
        id: trip.trip_id.toString(),
        date: trip.trip_date ? trip.trip_date.split("T")[0] : null,
        trip_date: trip.trip_date ? trip.trip_date.split("T")[0] : null,
        time: trip.pickup_time ? (() => { const h = parseInt(trip.pickup_time.split(":")[0]); return (h % 12 || 12) + ":" + trip.pickup_time.split(":")[1] + (h >= 12 ? " PM" : " AM"); })() : null,
        estimatedArrival: trip.estimated_arrival_time ? (() => { const h = parseInt(trip.estimated_arrival_time.split(":")[0]); return (h % 12 || 12) + ":" + trip.estimated_arrival_time.split(":")[1] + (h >= 12 ? " PM" : " AM"); })() : null,
        pickup: trip.pickup_venue,
        destination: trip.dropoff_location,
        capacity: trip.trip_passenger_capacity,
        driver: trip.driver,
        vehicle: trip.vehicle,
        contactNumber: trip.driver ? trip.driver.contact_number : null,
        purpose: trip.trip_purpose,
      }));
      
      // Formats the raw backend data into a UI-friendly structure for tables and lists
      setData(trips);
    } catch (error) {
      showErrorToast("Fetch Error", "Failed to load trips from server.");
    }
  }, [filterLocation, filterDriver, filterDate, filterStatus, ouberService]);

  // Sync selectedTrip if it's currently open in a modal and data refreshes
  useEffect(() => {
    if (selectedTrip && data.length > 0) {
      const updatedSelected = data.find(t => t.id === selectedTrip.id);
      if (updatedSelected && JSON.stringify(updatedSelected) !== JSON.stringify(selectedTrip)) {
        setSelectedTrip(updatedSelected);
      }
    }
  }, [data, selectedTrip, setSelectedTrip]);

  const fetchTripsRef = useRef(fetchTrips);
  useEffect(() => {
    fetchTripsRef.current = fetchTrips;
  }, [fetchTrips]);

  // Implements a debounced fetch to optimize server requests during live filtering
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchTripsRef.current();
    }, 500);
    return () => clearTimeout(handler);
  }, [filterLocation, filterDriver, filterDate, filterStatus]);


  // Generates and downloads a printable PDF Trip Ticket using the browser-based renderer
  const handlePrint = async (tripId) => {
    // Find the trip data from our current state
    const trip = data.find(t => t.id === tripId);
    if (!trip) {
      showErrorToast("Error", "Trip data not found.");
      return;
    }

    try {
      // Prepare contents for TripTicketFormat
      // We'll pass the trip record + AO name from profile
      const contents = {
        ...trip,
        aoName: `${profile?.first_name || ""} ${profile?.last_name || ""}`.trim()
      };

      // 3. Generate PDF using the system's native renderer
      const { pdf, Document, Page, View, StyleSheet } = await import("@react-pdf/renderer");
      const { default: TripTicketFormat } = await import("../../../generatePdf/documents/TripTicketFormat");

      const styles = StyleSheet.create({
        fullPage: {
          flexDirection: "column",
          backgroundColor: "#FFFFFF",
          fontFamily: "Arial",
        },
        fullSection: {
          width: "100%",
        },
      });

      const blob = await pdf(
        <Document>
          <Page size="A4" style={styles.fullPage}>
            <View style={styles.fullSection}>
              <TripTicketFormat contents={contents} />
            </View>
          </Page>
        </Document>
      ).toBlob();

      // Create filename: TripTicket_MMDDYYYY_Unit.pdf
      const datePart = dayjs(trip.trip_date).format("MMDDYYYY");
      const unitPart = (trip.organizing_unit || "ICTDO").replace(/[^A-Za-z0-9]/g, "");
      const filename = `TripTicket_${datePart}_${unitPart}.pdf`;

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      showErrorToast("Error", "Failed to generate printable ticket.");
    }
  };

  const filteredData = data;

  const handleEditTrip = (trip) => {
    setSelectedTrip(trip);
    setIsEditModalOpen(true);
  };

  const handleCreateTrip = () => {
    setIsAddModalOpen(true);
  };

  const handleViewTrip = (trip) => {
    setSelectedTrip(trip);
    setIsViewModalOpen(true);
  };

  const columns = [
    { title: "Trip ID", dataIndex: "trip_id", key: "trip_id", align: "center" },
    {
      title: "Trip Status",
      dataIndex: "trip_status",
      key: "trip_status",
      align: "center",
      render: (status, record) => {
        let displayStatus = status;
        
        // Final sanity check for live status if backend hasn't updated yet
        if (status !== 'Cancelled by AO' && status !== 'Cancelled by User' && status !== 'Completed Trip') {
          const now = dayjs();
          const tripDate = record.trip_date;
          const pickup = record.pickup_time; // HH:mm:ss
          const arrival = record.estimated_arrival_time;
          
          if (tripDate && pickup) {
            const start = dayjs(`${tripDate} ${pickup}`);
            const end = arrival ? dayjs(`${tripDate} ${arrival}`) : start.add(3, 'hour');
            
            if (now.isAfter(end)) displayStatus = 'Completed Trip';
            else if (now.isAfter(start)) displayStatus = 'In Progress';
          }

          // Deadline check
          const dDate = record.deadline_date || (record.trip_date ? dayjs(record.trip_date).subtract(1, 'day').format('YYYY-MM-DD') : null);
          const dTime = record.deadline_time || "17:00:00";
          if (dDate && displayStatus === 'Available') {
            const deadline = dayjs(`${dDate} ${dTime}`, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm", "YYYY-MM-DD h:mm a"]);
            if (now.isAfter(deadline)) displayStatus = 'Unavailable';
          }
        }

        let tagClass = "tag-unavailable";
        if (displayStatus === "Available") tagClass = "tag-available";
        if (displayStatus === "In Progress") tagClass = "tag-in-progress";
        if (displayStatus === "Completed Trip") tagClass = "tag-completed";
        if (displayStatus === "Cancelled by AO" || displayStatus === "Cancelled by User") tagClass = "tag-cancelled";
        
        return (
          <Tag
            className={tagClass}
            style={{
              borderRadius: "12px",
              minWidth: "80px",
              textAlign: "center",
            }}
          >
            {displayStatus}
          </Tag>
        );
      },
    },
    { 
      title: "Driver Name", 
      key: "driver", 
      align: "center",
      render: (_, record) => (record.driver && typeof record.driver === 'object' && record.driver.driver_name) ? record.driver.driver_name : (record.custom_driver_name || "Unassigned")
    },
    {
      title: "Trip Date",
      dataIndex: "trip_date",
      key: "trip_date",
      align: "center",
    },
    {
      title: "Pickup Venue",
      dataIndex: "pickup_venue",
      key: "pickup_venue",
      align: "center",
    },
    {
      title: "Destination",
      dataIndex: "dropoff_location",
      key: "dropoff_location",
      align: "center",
    },
    {
      title: "Vehicle Model",
      key: "vehicle",
      align: "center",
      render: (_, record) => (record.vehicle && typeof record.vehicle === 'object' && record.vehicle.vehicle_model) ? record.vehicle.vehicle_model : (record.custom_vehicle_name || "Unassigned")
    },
    {
      title: "Capacity",
      dataIndex: "trip_passenger_capacity",
      key: "trip_passenger_capacity",
      align: "center",
      render: (cap, record) => `${record.passengers ? record.passengers.length : 0}/${cap || 0}`,
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => {
        let displayStatus = record.trip_status;
        if (displayStatus !== 'Cancelled by AO' && displayStatus !== 'Cancelled by User' && displayStatus !== 'Completed Trip') {
          const now = dayjs();
          const tripDate = record.trip_date;
          const pickup = record.pickup_time;
          const arrival = record.estimated_arrival_time;
          
          if (tripDate && pickup) {
            const start = dayjs(`${tripDate} ${pickup}`);
            const end = arrival ? dayjs(`${tripDate} ${arrival}`) : start.add(3, 'hour');
            if (now.isAfter(end)) displayStatus = 'Completed Trip';
            else if (now.isAfter(start)) displayStatus = 'In Progress';
          }
        }

        const canEdit = isSuperAdmin || (userUnits.includes(record.organizing_unit) && !["Cancelled by AO", "Completed Trip", "Completed"].includes(displayStatus));
        
        return (
          <Space size="middle">
            <EyeOutlined
              style={{ fontSize: "18px", cursor: "pointer", color: "#1890ff" }}
              onClick={() => handleViewTrip(record)}
            />
            {canEdit && (
              <EditOutlined
                style={{ fontSize: "18px", cursor: "pointer", color: "#52c41a" }}
                onClick={() => handleEditTrip(record)}
              />
            )}
            <PrinterOutlined 
               style={{ fontSize: "18px", cursor: "pointer", color: "#ca8a04" }}
               onClick={() => handlePrint(record.id)}
            />
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <Space className="ouber-page-header">
        <Typography.Title level={3} className="ouber-page-title">
          Manage Trips
        </Typography.Title>
        <Button
          type="primary"
          icon={<PlusCircleOutlined />}
          className="ouber-btn-forest"
          onClick={handleCreateTrip}
        >
          Create Trip
        </Button>
      </Space>

      <Alert
        className="ouber-alert"
        message={<span className="ouber-alert-title">Manage Trips</span>}
        description={
          <span className="ouber-alert-desc">
            View, edit, and manage all trips from your respective unit. You can also print out trip tickets from here.
          </span>
        }
        type="info"
        showIcon
      />

      <div className="ouber-filter-container">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Input 
              placeholder="Search Driver Name" 
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={filterDriver}
              onChange={(e) => setFilterDriver(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input 
              placeholder="Search Pickup or Destination" 
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <DatePicker
              style={{ width: "100%" }}
              placeholder="Filter by Date"
              value={filterDate}
              onChange={(date) => setFilterDate(date)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter by Status"
              style={{ width: "100%" }}
              allowClear
              value={filterStatus}
              onChange={(val) => setFilterStatus(val)}
              options={[
                { value: "Completed Trip", label: "Completed Trip" },
                { value: "In Progress", label: "In Progress" },
                { value: "Available", label: "Available" },
                { value: "Unavailable", label: "Unavailable" },
                { value: "Cancelled by AO", label: "Cancelled by AO" }
              ]}
            />
          </Col>
        </Row>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: "12px" }}>
        <DataView />
        <Button 
          icon={<SyncOutlined />} 
          onClick={fetchTrips} 
          loading={loading}
          className="ouber-btn-primary"
          style={{ marginBottom: 0 }}
        >
          Sync Data
        </Button>
      </div>

      {dataView === "table" ? (
        <PvTable
          data={filteredData}
          columns={columns}
          loading={loading}
          onRowClick={() => {}}
        />
      ) : (
        <PvCardList
          data={filteredData}
          columns={columns}
          onRowClick={() => {}}
          loading={loading}
          maxActiveRow={filteredData.length}
        />
      )}

      <AdminAddTripModal 
        open={isAddModalOpen}
        onCancel={() => setIsAddModalOpen(false)}
        onSave={async (vals) => {
          try {
            await ouberService.createTrip(vals);
            showSuccessToast("Success", "Trip successfully added!");
            setIsAddModalOpen(false);
            fetchTrips();
          } catch(e) {
            showErrorToast("Error", "Could not add trip.");
          }
        }}
      />
      <AdminEditTripModal 
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        onSave={async (vals) => {
          try {
            await ouberService.updateTrip(selectedTrip.id, vals);
            showSuccessToast("Success", "Trip successfully updated!");
            setIsEditModalOpen(false);
            fetchTrips();
          } catch (e) {
            showErrorToast("Error", "Could not update trip.");
          }
        }}
        initialValues={selectedTrip}
        onDeleteTrip={(tripId) => {
          setIsEditModalOpen(false);
          setIsDeleteConfirmOpen(true);
        }}
        onUpdateSuccess={fetchTrips}
      />
      <AdminViewTripModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        tripData={selectedTrip}
        onUpdateSuccess={fetchTrips}
        onEditTrip={(trip) => {
          setIsViewModalOpen(false);
          setSelectedTrip(trip);
          setIsEditModalOpen(true);
        }}
        onDeleteTrip={(tripId) => {
          setIsViewModalOpen(false);
          setIsDeleteConfirmOpen(true);
        }}
      />
      <AdminConfirmDeleteTripModal 
        open={isDeleteConfirmOpen}
        onCancel={() => setIsDeleteConfirmOpen(false)}
        tripData={selectedTrip}
        loading={deleteLoading}
        onConfirm={async (trip) => {
          setDeleteLoading(true);
          try {
            await ouberService.deleteTrip(trip.id);
            showSuccessToast("Cancelled", "Trip cancelled and notifications sent.");
            setIsDeleteConfirmOpen(false);
            fetchTrips();
          } catch (e) {
            showErrorToast("Error", "Could not delete trip.");
          } finally {
            setDeleteLoading(false);
          }
        }}
      />
    </div>
  );
};

export default ManageTripsPage;
